import React, {useEffect, useRef, useState} from "react";
import {useAppDispatch, useAppSelector} from "redux/hooks";
import {Button} from "primereact/button";
import {checkPermissions} from "../../../../redux/permissions/permissionsUtils";
import {Calendar} from "primereact/calendar";
import {Dropdown} from "primereact/dropdown";
import {OverlayPanel} from "primereact/overlaypanel";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Paginator} from "primereact/paginator";
import {ConfirmDialog} from "primereact/confirmdialog";
import {SET_UPLOAD_FILE_STATUS_SUCCESS} from "../../../../redux/types/typesUsersList";
import {
    deleteAllTrunkStatisticsFiles,
    deleteTrunkStatisticsFileById,
    getActiveTrunksForStatistics,
    getTrunkStatisticExcel,
    getTrunkStatisticExcelByLink,
    getTrunkStatisticsFilesHistory,
} from "../../../../redux/api/apiTrunkStatisticsList";
import {
    setTrunkStatisticsFilesCurrentPage,
    setTrunkStatisticsFilesCurrentPageSize,
} from "../../../../redux/actions/actionsTrunkStatistics";

const TrunkStatisticsListNav = (props: any) => {
    const dispatch = useAppDispatch();
    const exportLoading = useAppSelector(state => state.TrunkStatisticsListReducer.exportLoading);
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);
    const rolesVision = useAppSelector(state => state.RolesReducer.rolesVision) as any;
    const statisticPermissions = checkPermissions(rolesVision, 'Statistic');
    const trunkStatisticsList = useAppSelector(state => state.TrunkStatisticsListReducer?.trunkStatisticsList);
    const filesHistory = useAppSelector(state => state.TrunkStatisticsListReducer?.filesHistory);
    const filesHistoryLoader = useAppSelector(state => state.TrunkStatisticsListReducer?.filesHistoryLoader);
    const filesPagination = useAppSelector(state => state.TrunkStatisticsListReducer?.filesPagination);
    const filesPaginationPage = useAppSelector(state => state.TrunkStatisticsListReducer?.filesPaginationPage);
    const filesPaginationSize = useAppSelector(state => state.TrunkStatisticsListReducer?.filesPaginationSize);

    const [hasTableData, setHasTableData] = useState(false);
    const [trunksOptions, setTrunksOptions] = useState<any[]>([]);
    const excelToast = useRef<any>(null);
    const [first, setFirst] = useState(0);
    const [filesTotalItems, setFilesTotalItems] = useState<number>(10);
    const [filesHistoryItems, setFilesHistoryItems] = useState<any>(filesHistory);
    const [newFileNotify, setNewFileNotify] = useState<boolean>(false);
    const [visibleDeleteAllFilesMessage, setVisibleDeleteAllFilesMessage] = useState(false);
    const [visible, setVisible] = useState(false);
    const [currentId, setCurrentId] = useState<any>(null);
    const [isFilesPanelVisible, setIsFilesPanelVisible] = useState(false);

    const minDate = new Date(2023, 5, 1);

    const getDefaultStartTime = () => new Date("2024-10-29 00:00:00");

    const getDefaultEndTime = () => {
        const d = new Date();
        d.setHours(23, 59, 59, 0);
        return d;
    };

    const getDefaultStartDate = () => new Date();
    const getDefaultEndDate = () => new Date();

    useEffect(() => {
        const rows = trunkStatisticsList?.trunks ?? trunkStatisticsList?.numbers ?? trunkStatisticsList?.items ?? [];
        setHasTableData(Array.isArray(rows) && rows.length > 0);
    }, [trunkStatisticsList]);

    useEffect(() => {
        setFilesHistoryItems(filesHistory);
    }, [filesHistory]);

    const handleGenerateTable = () => {
        dispatch(getTrunkStatisticExcel(filteredObject, jwtToken?.jwtToken))
            ?.then?.((isSuccess: boolean) => {
                if (isSuccess) {
                    setNewFileNotify(true);
                }
            });
    };

    useEffect(() => {
        if (filesPagination?.hasNext && filesPagination?.pageNumber) {
            setFilesTotalItems(filesPagination.pageNumber * filesPaginationSize + filesPaginationSize);
        } else if (!filesPagination?.hasNext) {
            setFilesTotalItems(filesPagination.pageNumber * filesPaginationSize);
        }
    }, [filesPagination, filesPaginationSize]);

    useEffect(() => {
        dispatch(setTrunkStatisticsFilesCurrentPage(1));
        dispatch(setTrunkStatisticsFilesCurrentPageSize(10));
    }, [dispatch]);

    useEffect(() => {
        if (!jwtToken?.jwtToken) return;
        getActiveTrunksForStatistics(jwtToken.jwtToken)
            .then((response) => {
                const items = response?.data?.data?.items ?? [];
                setTrunksOptions(
                    items.map((item: any) => {
                        const rawId = item.sofiaTrunkId ?? item.id;
                        const sofiaTrunkId = /^-?\d+$/.test(String(rawId)) ? Number(rawId) : rawId;
                        return {
                            ...item,
                            sofiaTrunkId,
                            displayName: `${sofiaTrunkId} ${item?.name ?? item?.profileName ?? ''}`.trim(),
                        };
                    }),
                );
            })
            .catch(() => setTrunksOptions([]));
    }, [jwtToken?.jwtToken]);

    useEffect(() => {
        if (props.selectedTrunk == null || !trunksOptions.length) {
            return;
        }
        const match = trunksOptions.find((item: any) =>
            String(item.sofiaTrunkId ?? item.id) === String(props.selectedTrunk)
        );
        if (!match) {
            return;
        }
        const matchedId = match.sofiaTrunkId ?? match.id;
        if (matchedId !== props.selectedTrunk) {
            props.setSelectedTrunk(matchedId);
        }
        props.onTrunkChange?.(match);
    }, [trunksOptions, props.selectedTrunk]);

    const {update: hasUpdatePermission} = statisticPermissions;

    const filteredObject = {
        sort: props.sortData?.sort,
        filter: props.sortData?.filter,
    };

    const isDisabled = () =>
        !props.endTime ||
        !props.startTime ||
        !props.filterDateTime ||
        !props.filterEndDateTime ||
        !props.selectedTrunk;

    const openFilesPanel = (e: React.SyntheticEvent) => {
        if (!isFilesPanelVisible) {
            dispatch(getTrunkStatisticsFilesHistory({
                pageNumber: filesPaginationPage || 1,
                pageSize: filesPaginationSize || 10,
            }, jwtToken?.jwtToken));
            dispatch({type: SET_UPLOAD_FILE_STATUS_SUCCESS, payload: null});
            setNewFileNotify(false);
        }
        setIsFilesPanelVisible(true);
        excelToast.current?.toggle(e);
    };

    const onFilesPageChange = (event: {first: number; page: number; rows: number}) => {
        setFirst(event.first);
        dispatch(setTrunkStatisticsFilesCurrentPage(event.page + 1));
        dispatch(setTrunkStatisticsFilesCurrentPageSize(event.rows));
        dispatch(getTrunkStatisticsFilesHistory({
            pageNumber: event.page + 1,
            pageSize: event.rows,
        }, jwtToken?.jwtToken));
    };

    const handleUpload = (id: number) => {
        dispatch(getTrunkStatisticExcelByLink(id, jwtToken?.jwtToken));
    };

    const uploadByUrl = (fileUrl: any, fileName: any) => {
        if (fileUrl) {
            const link = document.createElement('a');
            link.href = fileUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    };

    const handleDeleteFile = (id: number) => {
        dispatch(deleteTrunkStatisticsFileById(id, {
            pageNumber: filesPaginationPage || 1,
            pageSize: filesPaginationSize || 10,
        }, jwtToken?.jwtToken));
    };

    const acceptDeleteFile = () => {
        handleDeleteFile(currentId);
        setVisible(false);
    };

    const bodyExcelUpload = (rowData: any) => (
        <div className="flex justify-content-center">
            {!rowData?.tempUrl ? (
                <Button onClick={() => handleUpload(rowData?.id)} icon="pi pi-download" className="p-button-secondary ml-auto mr-2" />
            ) : (
                <Button
                    icon="pi pi-download"
                    className="p-button-secondary ml-auto mr-2"
                    onClick={() => uploadByUrl(rowData?.tempUrl, rowData?.fileName)}
                />
            )}
            <Button
                onClick={() => {
                    setCurrentId(rowData?.id);
                    setVisible(true);
                }}
                icon="pi pi-trash"
                className="p-button-secondary mx-auto"
            />
        </div>
    );

    const bodyExcelId = (rowData: any) => (
        <div className="flex justify-content-center">{rowData?.id}</div>
    );

    const renderFilesButton = (className = 'mr-2 mb-2') => (
        <Button
            style={{backgroundColor: '#1d6f42'}}
            className={`p-button-success text-white ${className} ${newFileNotify ? 'animate-bounce-loop' : 'shadow-none'}`}
            type="button"
            icon="pi pi-download"
            onClick={openFilesPanel}
        />
    );

    const renderFilesDialogs = () => (
        <>
            <ConfirmDialog
                style={{zIndex: 9999999999999}}
                visible={visible}
                onHide={() => setVisible(false)}
                message="Файл буде видалено зі списку, продовжити ?"
                header="Попередження"
                acceptLabel="Так"
                rejectLabel="Ні"
                icon="pi pi-exclamation-triangle"
                accept={acceptDeleteFile}
                reject={() => setVisible(false)}
            />
            <ConfirmDialog
                visible={visibleDeleteAllFilesMessage}
                style={{zIndex: 99999999999999}}
                onHide={() => setVisibleDeleteAllFilesMessage(false)}
                message="Всі файли будуть видалені, продовжити ?"
                header="Попередження"
                acceptLabel="Так"
                rejectLabel="Ні"
                icon="pi pi-exclamation-triangle"
                accept={() => {
                    setVisibleDeleteAllFilesMessage(false);
                    dispatch(deleteAllTrunkStatisticsFiles(jwtToken?.jwtToken));
                    excelToast.current?.hide();
                }}
                reject={() => setVisibleDeleteAllFilesMessage(false)}
            />
            <OverlayPanel
                className="excelPanel"
                ref={excelToast}
                style={{minWidth: '38rem'}}
                onHide={() => setIsFilesPanelVisible(false)}
            >
                <div className="flex justify-content-end align-items-center">
                    <Button
                        type="button"
                        disabled={filesHistory?.length < 1}
                        label="Видалити все"
                        icon="pi pi-trash"
                        className="p-button-outlined mr-4"
                        onClick={() => {
                            excelToast.current?.hide();
                            setVisibleDeleteAllFilesMessage(true);
                        }}
                    />
                    <Button
                        onClick={() => {
                            excelToast.current?.hide();
                            setNewFileNotify(false);
                        }}
                        icon="pi pi-times"
                        className="p-button-secondary"
                    />
                </div>
                <DataTable
                    loading={filesHistoryLoader}
                    emptyMessage="Файлів поки немає"
                    style={{minWidth: '300px'}}
                    value={filesHistoryItems}
                >
                    <Column field="id" header="ID" body={bodyExcelId} />
                    <Column field="fileName" header="Назва" />
                    <Column field="code" header="Дія" style={{maxWidth: '100px'}} body={bodyExcelUpload} />
                </DataTable>
                <Paginator
                    first={first}
                    rows={filesPaginationSize || 10}
                    totalRecords={filesTotalItems}
                    rowsPerPageOptions={[5, 10, 15, 25, 50]}
                    onPageChange={onFilesPageChange}
                    className="border-top-1 border-bottom-1"
                    template="PrevPageLink PageLinks NextPageLink RowsPerPageDropdown"
                />
            </OverlayPanel>
        </>
    );

    const renderDateInputHandlers = (
        setDate: (v: Date | null) => void,
        getDefault: () => Date,
        getCurrent: () => Date | null,
    ) => ({
        onInput: (e: any) => {
            const input = e.target as HTMLInputElement;
            const digits = input.value.replace(/\D/g, "").slice(0, 8);
            const dd = digits.slice(0, 2);
            const mm = digits.slice(2, 4);
            const yyyy = digits.slice(4, 8);
            const display =
                (dd || "") +
                (digits.length > 2 ? "/" + mm : "") +
                (digits.length > 4 ? "/" + yyyy : "");
            const newCaret =
                digits.length +
                (digits.length > 2 ? 1 : 0) +
                (digits.length > 4 ? 1 : 0);
            if (input.value !== display) {
                input.value = display;
                input.setSelectionRange(newCaret, newCaret);
            }
            if (digits.length === 8) {
                const day = parseInt(dd, 10);
                const month = parseInt(mm, 10) - 1;
                const year = parseInt(yyyy, 10);
                const parsed = new Date(year, month, day);
                const valid =
                    !isNaN(parsed.getTime()) &&
                    parsed.getDate() === day &&
                    parsed.getMonth() === month &&
                    parsed.getFullYear() === year &&
                    parsed >= minDate;
                setDate(valid ? parsed : null);
            }
        },
        onFocus: () => setDate(null),
        onBlur: (e: any) => {
            const digits = e.target.value.replace(/\D/g, "");
            if (digits.length !== 8) {
                e.target.value = "";
                setDate(null);
            }
            if (!getCurrent()) {
                setDate(getDefault());
            }
        },
    });

    const renderTimeInputHandlers = (
        setTime: (v: Date | null) => void,
        getCurrent: () => Date | null,
        getDefault: () => Date,
    ) => ({
        onFocus: () => setTime(null),
        onInput: (e: any) => {
            const input = e.target as HTMLInputElement;
            const digits = input.value.replace(/\D/g, "").slice(0, 4);
            const HH = digits.slice(0, 2);
            const MM = digits.slice(2, 4);
            const display = HH + (digits.length > 2 ? ":" + MM : "");
            if (input.value !== display) {
                const caret = digits.length + (digits.length > 2 ? 1 : 0);
                input.value = display;
                input.setSelectionRange(caret, caret);
            }
            if (digits.length === 4) {
                const h = parseInt(HH, 10);
                const m = parseInt(MM, 10);
                const valid = h >= 0 && h <= 23 && m >= 0 && m <= 59;
                if (valid) {
                    const base = getCurrent() ? new Date(getCurrent()!) : new Date();
                    base.setHours(h, m, 0, 0);
                    setTime(base);
                }
            }
        },
        onBlur: () => {
            if (!getCurrent()) setTime(getDefault());
        },
    });

    const trunkDropdown = (className?: string, style?: React.CSSProperties) => (
        <Dropdown
            value={props.selectedTrunk}
            options={trunksOptions}
            onChange={(e) => {
                props.setSelectedTrunk(e.value);
                const trunk = trunksOptions.find(
                    (item: any) => (item.sofiaTrunkId ?? item.id) === e.value,
                );
                props.onTrunkChange?.(trunk ?? null);
            }}
            optionValue="sofiaTrunkId"
            placeholder="Оберіть транк:"
            className={className ?? "mt-2 mr-2"}
            style={style ?? {minWidth: '250px'}}
            filter
            optionLabel="displayName"
            scrollHeight="250px"
        />
    );

    return (
        <>
            <div className="table-buttons trunk-statistics-nav">
                <div className="text-normal-weight flex align-items-center">
                    <div className="flex field align-items-center desktop-filter">
                        {trunkDropdown()}
                    </div>
                    <div className="flex field align-items-center max-w-15rem ml-4 desktop-filter">
                        <span className="title pt-2" style={{width: '35px'}}>З*</span>
                        <Calendar
                            className="mt-2"
                            value={props.filterDateTime ?? null}
                            onChange={(e: any) => props.setFilterDateTime(e.value)}
                            {...renderDateInputHandlers(props.setFilterDateTime, getDefaultStartDate, () => props.filterDateTime)}
                            minDate={minDate}
                            maxDate={new Date()}
                            dateFormat="dd/mm/yy"
                            placeholder="дд/мм/рр"
                        />
                        <Calendar
                            appendTo="self"
                            className="mt-2 ml-2"
                            placeholder="00:00"
                            value={props.startTime ?? null}
                            timeOnly
                            hourFormat="24"
                            onChange={(e) => e.value && props.setStartTime(e.value)}
                            {...renderTimeInputHandlers(props.setStartTime, () => props.startTime, getDefaultStartTime)}
                        />
                    </div>
                    <div className="flex field align-items-center max-w-15rem ml-2 desktop-filter">
                        <span className="title pt-2" style={{width: '35px'}}>По*</span>
                        <Calendar
                            className="mt-2"
                            value={props.filterEndDateTime ?? null}
                            onChange={(e: any) => props.setFilterEndDateTime(e.value)}
                            {...renderDateInputHandlers(props.setFilterEndDateTime, getDefaultEndDate, () => props.filterEndDateTime)}
                            minDate={minDate}
                            maxDate={new Date()}
                            dateFormat="dd/mm/yy"
                            placeholder="дд/мм/рр"
                        />
                        <Calendar
                            appendTo="self"
                            className="mt-2 ml-2"
                            placeholder="00:00"
                            value={props.endTime ?? null}
                            timeOnly
                            hourFormat="24"
                            onChange={(e) => e.value && props.setEndTime(e.value)}
                            {...renderTimeInputHandlers(props.setEndTime, () => props.endTime, getDefaultEndTime)}
                        />
                    </div>
                    <Button
                        disabled={isDisabled()}
                        onClick={() => props.onSearch?.()}
                        icon="pi pi-search"
                        className="mb-2 ml-2 desktop-filter"
                    />
                </div>
                <div className="desktop">
                    <div className="flex align-items-center">
                        {hasUpdatePermission && renderFilesButton()}
                        {hasUpdatePermission && (
                            <Button
                                onClick={handleGenerateTable}
                                label="Сформувати таблицю"
                                loading={exportLoading}
                                disabled={!hasTableData}
                                icon="pi pi-file-excel"
                                style={{backgroundColor: '#1d6f42'}}
                                className="mr-2 mb-2 p-button-success text-white shadow-none"
                            />
                        )}
                        <Button
                            type="button"
                            icon="pi pi-filter-slash"
                            label="Очистити фільтри"
                            className="p-button-outlined mr-2 mb-2"
                            onClick={props.resetSorting}
                        />
                    </div>
                </div>
                <div className="mobile">
                    <div className="flex align-items-center">
                        {hasUpdatePermission && renderFilesButton()}
                        {hasUpdatePermission && (
                            <Button
                                onClick={handleGenerateTable}
                                icon="pi pi-file-excel"
                                loading={exportLoading}
                                disabled={!hasTableData}
                                style={{backgroundColor: '#1d6f42'}}
                                className="mr-2 mb-2 p-button-success text-white shadow-none"
                            />
                        )}
                        <Button
                            type="button"
                            icon="pi pi-filter-slash"
                            className="p-button-outlined mr-2 mb-2"
                            onClick={props.resetSorting}
                        />
                    </div>
                </div>
            </div>
            <div className="trunk-statistics-filters">
                <div className="trunk-statistics-filters__trunk">
                    {trunkDropdown("mt-2 w-full", {maxWidth: '100%'})}
                </div>
                <div className="trunk-statistics-filters__controls">
                    <div className="trunk-statistics-filters__date-group">
                        <span className="title pt-2" style={{minWidth: '25px'}}>З*</span>
                        <Calendar
                            className="mt-2 trunk-statistics-filters__date w-full"
                            value={props.filterDateTime ?? null}
                            onChange={(e: any) => props.setFilterDateTime(e.value)}
                            {...renderDateInputHandlers(props.setFilterDateTime, getDefaultStartDate, () => props.filterDateTime)}
                            minDate={minDate}
                            maxDate={new Date()}
                            dateFormat="dd/mm/yy"
                            placeholder="дд/мм/рр"
                        />
                        <Calendar
                            appendTo="self"
                            className="mt-2 ml-2 trunk-statistics-filters__time"
                            placeholder="00:00"
                            value={props.startTime ?? null}
                            timeOnly
                            hourFormat="24"
                            onChange={(e) => e.value && props.setStartTime(e.value)}
                            {...renderTimeInputHandlers(props.setStartTime, () => props.startTime, getDefaultStartTime)}
                        />
                    </div>
                    <div className="trunk-statistics-filters__date-group">
                        <span className="title pt-2" style={{minWidth: '25px'}}>По*</span>
                        <Calendar
                            className="mt-2 trunk-statistics-filters__date w-full"
                            value={props.filterEndDateTime ?? null}
                            onChange={(e: any) => props.setFilterEndDateTime(e.value)}
                            {...renderDateInputHandlers(props.setFilterEndDateTime, getDefaultEndDate, () => props.filterEndDateTime)}
                            minDate={minDate}
                            maxDate={new Date()}
                            dateFormat="dd/mm/yy"
                            placeholder="дд/мм/рр"
                        />
                        <Calendar
                            appendTo="self"
                            className="mt-2 ml-2 trunk-statistics-filters__time"
                            placeholder="00:00"
                            value={props.endTime ?? null}
                            timeOnly
                            hourFormat="24"
                            onChange={(e) => e.value && props.setEndTime(e.value)}
                            {...renderTimeInputHandlers(props.setEndTime, () => props.endTime, getDefaultEndTime)}
                        />
                    </div>
                    <Button
                        onClick={() => props.onSearch?.()}
                        disabled={isDisabled()}
                        icon="pi pi-search"
                        label="Пошук"
                        className="trunk-statistics-filters__search mobile-filter-btn"
                        style={{minWidth: '30px'}}
                    />
                </div>
            </div>
            {hasUpdatePermission && renderFilesDialogs()}
        </>
    );
};

export default TrunkStatisticsListNav;
